// ──────────────────────────────────────────────────────
// Trust — frontend API client.
//
// Public calls (TrustModal, TrustBadge consumers) are anonymous and hit
// /api/trust/... through Next.js (see matching proxy). Admin calls go
// through adminFetchJson (JWT-guarded).
// ──────────────────────────────────────────────────────
import { adminFetchJson } from './admin-auth';

export interface TrustDocument {
    id: string;
    title: string;
    issuerName: string | null;
    supplierName: string | null;
    descriptionAr: string | null;
    scopeType: string;
    fileKey: string;
    mimeType: string;
    fileSizeBytes: number;
    issuedAt: string | null;
    expiresAt: string | null;
    brandsCovered: string[] | null;
    notes: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface UploadInput {
    title: string;
    issuerName?: string | null;
    supplierName?: string | null;
    descriptionAr?: string | null;
    issuedAt?: string | null;
    expiresAt?: string | null;
    brandsCovered?: string[] | null;
    notes?: string | null;
    mimeType: string;
    dataBase64: string;
}

// Matches the convention used throughout apps/kiosk-web/lib/admin-auth.ts
// — all backend calls go through the Next.js [...path] proxy at
// app/api/backend/.
const API_BASE = '/api/backend';

// ── Public (customer-facing) ──────────────────────────────────

export async function listPublicTrustDocuments(): Promise<TrustDocument[]> {
    const res = await fetch(`${API_BASE}/trust/documents`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`trust fetch failed: ${res.status}`);
    const body = await res.json();
    return body.items || [];
}

/** Returns the full URL for inline PDF preview/download. */
export function getPublicTrustFileUrl(id: string): string {
    return `${API_BASE}/trust/documents/${encodeURIComponent(id)}/file`;
}

// ── Admin ─────────────────────────────────────────────────────

export async function listAdminTrustDocuments(): Promise<TrustDocument[]> {
    const res = await adminFetchJson<{ items: TrustDocument[] }>('/admin/trust/documents');
    return res.items || [];
}

export async function uploadTrustDocument(input: UploadInput): Promise<TrustDocument> {
    return adminFetchJson<TrustDocument>('/admin/trust/documents', {
        method: 'POST',
        body: JSON.stringify(input),
    });
}

export async function updateTrustDocument(
    id: string,
    patch: Partial<Pick<TrustDocument, 'title' | 'issuerName' | 'supplierName' | 'descriptionAr' | 'issuedAt' | 'expiresAt' | 'brandsCovered' | 'notes' | 'isActive'>>,
): Promise<TrustDocument> {
    return adminFetchJson<TrustDocument>(`/admin/trust/documents/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    });
}

export async function deleteTrustDocument(id: string): Promise<void> {
    await adminFetchJson(`/admin/trust/documents/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function getAdminTrustFileUrl(id: string): string {
    return `${API_BASE}/admin/trust/documents/${encodeURIComponent(id)}/file`;
}
